import {
  Badge,
  Link as FluentLink,
  TableCell,
  TableRow,
  Tooltip,
  TableCellLayout,
  tokens,
  makeStyles,
  Caption1,
} from '@fluentui/react-components';
import { EditRegular, TagRegular, WarningFilled } from '@fluentui/react-icons';
import { useDrag, useDrop } from 'react-dnd';

import AvatarGroup from '@modules/users/components/AvatarGroup';
import UserLabel from '@modules/users/components/UserLabel';
import SignInContext from '@modules/users/contexts/SignInContext.context';
import { AgoDateLabel } from '@shared/components/dates/AgoDateLabel';
import { DateLabel } from '@shared/components/dates/DateLabel';
import { DueDateLabel, type TDue } from '@shared/components/dates/DueDateLabel';
import { IconLabel } from '@shared/components/IconLabel';
import { Path } from '@shared/components/Path';
import { SmartTableCell, type SmartTableCellProps } from '@shared/components/tables/SmartTableCell';
import { TableActionButtons } from '@shared/components/tables/TableActionButtons';
import { getBlockTagLabel } from '@shared/utils/tagUtils';
import { LegacyRef, MouseEventHandler, useContext, useEffect, useRef, useState } from 'react';
import { AccessType } from '@modules/access/types';
import { EFolderType } from '@modules/items/types/folders.types';
import type { TOnMove, TPath } from '@modules/items/types/folders.types';
import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';
import type {
  TDisableColumn,
  TItemsTableRow,
  TItem,
  TShowColumn,
  TResolvedItemNames,
} from '@modules/items/types/items.types';
import { EDashboardReviewState } from '@modules/items/types/itemClassification.types';
import StatusBadge from './StatusBadge';
import { ITableColumnSizingState } from '@shared/types';
import { isTSearchItem } from '@modules/search/types';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks/useFolderPathAsBreadcrumbs.hook';
import { buildHref } from '@shared/utils/url';
import { dateToYMD, localDateToYMD } from '@shared/utils/date';

type TProps = TItemsTableRow & {
  resolvedNames?: TResolvedItemNames;
  dateType: 'modified' | 'viewed' | 'archived';
  relativeDate: boolean;
  show: TShowColumn;
  disable: TDisableColumn;
  openMode?: SmartTableCellProps['openMode'];
  onLinkClick: MouseEventHandler<HTMLAnchorElement>;
  onMove?: TOnMove;
  columnSizing?: ITableColumnSizingState;
  folderType?: EFolderType;
};

const useStyles = makeStyles({
  previewNotAvailable: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  userName: {
    display: 'block',
    maxWidth: '118px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  overdue: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
});

const getReviewDue = (classification: TItem['classification']): TDue | undefined => {
  const days = classification?.daysUntilReview;
  if (days === undefined) return undefined;
  if (classification?.periodicReview === EDashboardReviewState.OVERDUE) return { state: 'OVERDUE', days };
  if (classification?.periodicReview === EDashboardReviewState.DUE_SOON) return { state: 'DUE_SOON', days };
  return undefined;
};

const ApprovalOpenSinceTableCell = ({ dateOpened }: { dateOpened?: string }) => {
  const classes = useStyles();
  return (
    <TableCell className={classes.nowrap}>
      {dateOpened ? <AgoDateLabel short>{dateOpened}</AgoDateLabel> : '-'}
    </TableCell>
  );
};

const ReviewDueTableCell = ({ item }: { item: TItem }) => {
  const classes = useStyles();
  return (
    <TableCell className={classes.nowrap}>
      <DueDateLabel
        variant="compact"
        dueDate={item.periodicReview?.enabled ? item.periodicReview.nextReviewDate : undefined}
        due={getReviewDue(item.classification)}
      />
    </TableCell>
  );
};

const ReviewerTableCell = ({ item }: { item: TItem }) => {
  const classes = useStyles();
  const reviewerId = item.periodicReview?.responsibleUser;

  return (
    <TableCell>
      {reviewerId ? <UserLabel id={reviewerId} avatarProps={{ size: 28 }} className={classes.userName} /> : '-'}
    </TableCell>
  );
};

const TableItem = ({
  resolvedNames,
  dateType,
  relativeDate,
  show,
  disable,
  openMode,
  onLinkClick,
  onMove,
  item,
  columnSizing,
  folderType,
  rootName,
  unavailableLabel,
  approvalDateOpened,
  submissionDueDate,
  openApprovals,
  selectionCell = null,
  actionButton = null,
  ...tableRowAttributes
}: TProps) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'item',
    item,
    canDrag: Boolean(onMove),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'item',
    drop: (dropItem: TItem) => {
      onMove?.(dropItem._id, dropItem.type, item._id);
    },
    canDrop: (dropItem) => dropItem._id !== item._id && item.type === 'FOLDER',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const ref = useRef<HTMLDivElement>();

  // --- location column (breadcrumbs)
  const locationPath: TPath = item.path.map((id) => ({
    _id: id,
    name: resolvedNames?.[id] || id,
    accessRight: AccessType.NONE, // @Jan – do you think it's correct to set this to NONE?
    target: '_blank',
    disabled: disable.location,
  }));
  const locationItems = useFolderPathAsBreadcrumbs(locationPath);

  const registrationProducts = item.countryRegistration?.products || [];
  const resolvedProductLabels = registrationProducts
    .map((productId) => resolvedNames?.[productId])
    .filter((label): label is string => Boolean(label));
  const registrationProductLabels =
    resolvedProductLabels.length === registrationProducts.length
      ? resolvedProductLabels
      : registrationProducts.map((_, idx) => `Product ${idx + 1}`);

  const badgesRef = useRef<HTMLDivElement>(null);
  const [visibleBadgeCount, setVisibleBadgeCount] = useState(registrationProductLabels.length);

  useEffect(() => {
    const el = badgesRef.current;
    if (!el) return;
    const measure = () => {
      const containerRight = el.getBoundingClientRect().right;
      let count = 0;
      for (const child of Array.from(el.children)) {
        if (child.getBoundingClientRect().right <= containerRight + 1) count++;
        else break;
      }
      setVisibleBadgeCount(count);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [registrationProductLabels.length]);

  const hiddenBadgeCount = registrationProductLabels.length - visibleBadgeCount;

  const { organization } = useContext(SignInContext);
  const blockTagLabel = item.blockTagId ? getBlockTagLabel(organization.blockTags, item.blockTagId) : undefined;

  const classes = useStyles();

  return (
    <TableRow
      ref={drag(drop(ref)) as LegacyRef<HTMLDivElement>}
      {...tableRowAttributes}
      style={{
        opacity: isDragging ? 0.4 : 1,
        backgroundColor: isOver ? '#f2f2f2' : '',
        outline: isOver && canDrop ? `2px dashed ${tokens.colorBrandStroke1}` : '',
        borderRadius: '4px',
      }}
    >
      {selectionCell}

      {/* 1) Name column */}
      <SmartTableCell
        cellProps={{ ...(columnSizing?.getTableCellProps('name') ?? {}) }}
        variant={disable.name ? { kind: 'text', label: item.name } : { kind: 'item', item }}
        openMode={openMode}
        forcePlainText={!!unavailableLabel}
        linkDisabled={!!unavailableLabel}
        descriptionHtml={rootName ?? (isTSearchItem(item) ? item.snippet : item.recentData?.location)}
        after={actionButton}
        tooltip={{ threshold: 70 }}
        stackedBelow={
          folderType === EFolderType.BLOCK && item.type === 'INLINE_BLOCK' && blockTagLabel ? (
            <Caption1 className="text-neutralFg-2">
              <IconLabel icon={<TagRegular />}>{blockTagLabel}</IconLabel>
            </Caption1>
          ) : undefined
        }
      />

      {show.approvalOpenSince && <ApprovalOpenSinceTableCell dateOpened={approvalDateOpened} />}

      {show.reviewDue && <ReviewDueTableCell item={item} />}

      {show.submissionDue && (
        <TableCell {...columnSizing?.getTableCellProps('submissionDue')} className={classes.nowrap}>
          {submissionDueDate ? (
            <span className={dateToYMD(submissionDueDate) < localDateToYMD() ? classes.overdue : undefined}>
              <DateLabel short>{submissionDueDate}</DateLabel>
            </span>
          ) : (
            '-'
          )}
        </TableCell>
      )}

      {show.openApprovals && (
        <TableCell {...columnSizing?.getTableCellProps('openApprovals')}>
          {openApprovals ? (
            <Badge appearance="tint" color="warning">
              {openApprovals} open
            </Badge>
          ) : (
            '-'
          )}
        </TableCell>
      )}

      {show.products && (
        <TableCell {...(columnSizing?.getTableCellProps('products') ?? {})}>
          {registrationProducts.length > 0 ? (
            <div className="flex max-w-full items-center gap-1">
              <div ref={badgesRef} className="flex min-w-0 gap-1 overflow-hidden">
                {registrationProductLabels.map((label, idx) => (
                  <Badge key={`${registrationProducts[idx]}-${idx}`} appearance="tint">
                    <span className="inline-block max-w-[120px] truncate align-bottom" title={label}>
                      {label}
                    </span>
                  </Badge>
                ))}
              </div>
              {hiddenBadgeCount > 0 && (
                <Tooltip content={registrationProductLabels.slice(visibleBadgeCount).join(', ')} relationship="label">
                  <Badge className="shrink-0" appearance="tint">
                    +{hiddenBadgeCount}
                  </Badge>
                </Tooltip>
              )}
            </div>
          ) : (
            '-'
          )}
        </TableCell>
      )}

      {show.location && resolvedNames && (
        <TableCell {...columnSizing?.getTableCellProps('location')}>
          <Path items={locationItems} hasCurrent={false} size="small" />
        </TableCell>
      )}

      {show.preview && (
        <TableCell {...columnSizing?.getTableCellProps('preview')}>
          {item.type === 'DOCUMENT' ? (
            <span className={classes.previewNotAvailable}>Preview not available for content.</span>
          ) : item.type === 'INLINE_BLOCK' ? (
            <span>{item.preview || ''}</span>
          ) : null}
        </TableCell>
      )}

      {/* 2) Modified/Opened/Archived column */}
      {show.date && (
        <TableCell {...columnSizing?.getTableCellProps(dateType)}>
          {dateType === 'modified' && relativeDate ? (
            <AgoDateLabel>{item.stats.modifiedDate}</AgoDateLabel>
          ) : dateType === 'modified' ? (
            <DateLabel short>{item.stats.modifiedDate}</DateLabel>
          ) : dateType === 'viewed' && item.recentData ? (
            <AgoDateLabel>{item.recentData.viewedDate}</AgoDateLabel>
          ) : dateType === 'archived' && item.stats.archivedDate ? (
            <DateLabel withTime short>
              {item.stats.archivedDate}
            </DateLabel>
          ) : (
            '-'
          )}
        </TableCell>
      )}

      {/* 3) Status column */}
      {show.status && (
        <TableCell {...columnSizing?.getTableCellProps('status')}>
          <StatusBadge item={item} unavailableLabel={unavailableLabel} />
        </TableCell>
      )}

      {/* 4) Latest version column */}
      {show.version && (
        <TableCell {...columnSizing?.getTableCellProps('latestVersionId')}>
          <div className="flex space-x-2 items-center">
            {item.stats.latestVersionId && !unavailableLabel ? (
              <FluentLink
                href={buildHref(`/${getItemPathByItemType(item.type)}/${item._id}`, {
                  view: item.stats.latestVersionId,
                })}
                target="_blank"
                onClick={onLinkClick}
              >
                {item.stats.latestVersionId}
              </FluentLink>
            ) : (
              <span>-</span>
            )}
            {item.hasDraft && !unavailableLabel && (
              <Tooltip content={`This item has an open draft.`} relationship="label">
                <EditRegular />
              </Tooltip>
            )}
          </div>
        </TableCell>
      )}

      {/* 5) Approved version column */}
      {show.approvedVersion && (
        <TableCell {...columnSizing?.getTableCellProps('approvedVersionId')}>
          {item.stats.approvedVersionId && !unavailableLabel ? (
            <FluentLink
              href={buildHref(`/${getItemPathByItemType(item.type)}/${item._id}`, {
                view: item.stats.approvedVersionId,
              })}
              target="_blank"
              onClick={onLinkClick}
            >
              {item.stats.approvedVersionId}
            </FluentLink>
          ) : (
            '-'
          )}
        </TableCell>
      )}

      {/* 6) Authors column (if showAuthors) */}
      {show.authors && (
        <TableCell {...columnSizing?.getTableCellProps('authors')}>
          <TableCellLayout>{unavailableLabel ? '-' : <AvatarGroup ids={item.stats.authors} />}</TableCellLayout>
          {item.numErrors > 0 && !unavailableLabel && (
            <TableActionButtons alwaysVisible>
              <Tooltip
                content={`${item.hasDraft ? 'Draft' : 'Latest version'} has ${item.numErrors} errors.`}
                relationship="label"
                positioning="above-end"
              >
                <WarningFilled fontSize={20} color="#c43501" />
              </Tooltip>
            </TableActionButtons>
          )}
        </TableCell>
      )}

      {show.reviewer && <ReviewerTableCell item={item} />}

      {show.archivedBy &&
        item.stats.archivedDate && ( // we have to use archivedDate as archivedByUser is only available for new items
          <TableCell {...columnSizing?.getTableCellProps('archivedBy')}>
            {item.stats.archivedByUser ? <AvatarGroup ids={[item.stats.archivedByUser]} /> : '-'}
          </TableCell>
        )}
    </TableRow>
  );
};

export default TableItem;
